// ProjectsView — Projects workspace (Project screen BUILD 1 + D-refinements).
//
// Reuse proof: HierarchyWorkspace (tree + splitter + main shell) + DataGrid +
// Breadcrumb consumed UNCHANGED (A47).
// Two screens:
//   1. List screen — pick / create a project (code · customer · status · total).
//   2. Interior — HierarchyWorkspace: function tree (left) + function grid (DataGrid,
//                 centre) + placements grid (DataGrid) for a selected function.
//
// D-refinements applied (mirrors ProductsView 1b standard):
//   - Draggable tree-width splitter (localStorage-persisted) — now via HierarchyWorkspace.
//   - Function grid: #-first, Code, Label, path/move dropdowns (edit-mode-gated),
//     reorder (▲▼ via sort_order), per-row edit mode, inline-add, non-empty-delete warning.
//   - Placements grid: #-first, consistent columns.
//
// Component lanes (A53): no edits to src/web/components/. Any component-change
// candidates are filed as NOTEs below, not forked here.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DataGrid from './components/DataGrid.jsx';
import AssistantSection from './components/AssistantSection.jsx';
import Notice from './components/Notice.jsx';
import Breadcrumb from './components/Breadcrumb.jsx';
import CategoryOnshapePanel from './components/CategoryOnshapePanel.jsx';
import HierarchyWorkspace from './components/HierarchyWorkspace.jsx';
import ViewSwitcher from './components/ViewSwitcher.jsx';
import CascadeSelect, { CascadeDropdown, useCascadeSignal } from './components/CascadeSelect.jsx';
import PlacementConfigSubRow from './components/PlacementConfigSubRow.jsx';
import OnshapeSection from './components/OnshapeSection.jsx';
import PlacementSidePanel from './components/PlacementSidePanel.jsx';
import HeaderRow from './components/HeaderRow.jsx';
import Card from './components/Card.jsx';
import PictureDrop from './components/PictureDrop.jsx';
import { PRODUCT, LINK, WARN } from './icons.js';
import Chevron from './components/Chevron.jsx';
import { formatMoney, formatQty } from './formatMoney.js';
import MoneyCell from './MoneyCell.jsx';
import Text from './components/Text.jsx';
import OwnerLabel from './components/OwnerLabel.jsx';

import { NAVY, PROJ_META, BTN_SECONDARY, BTN_GLYPH, PANE_GAP } from './theme-tokens.js';

// Stable empty object so an un-edited expanded row does not get a new `values`
// identity on every render.
const EMPTY_EDITS = {};

// Project lifecycle (A44) lives in theme-tokens.js as PROJ_META (shared with
// ProjectsByCustomerView) — color-blind: icon + word, never color alone.

// Procurement status (A52) — placement axis.
const PROC_META = {
  estimated: { icon: '◷', word: 'Estimated', color: 'var(--th-text-secondary)' },
  approved:  { icon: '◑', word: 'Approved',  color: 'var(--th-navy)' },
  purchased: { icon: '🔒', word: 'Purchased', color: 'var(--th-status-validated)' },
  received:  { icon: '✓', word: 'Received',  color: 'var(--th-status-synced)' },
};

function Badge({ meta }) {
  if (!meta) return <span style={{ color: 'var(--th-text-disabled)' }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)', color: meta.color }}>
      <span style={{ fontWeight: 700 }}>{meta.icon}</span>{meta.word}
    </span>
  );
}

const money = (n) => formatMoney(n ?? 0);

// Walk the function tree to find path from root → target (for breadcrumb + node lookup).
function findFuncPath(nodes, targetId, trail = []) {
  for (const n of nodes || []) {
    const here = [...trail, n];
    if (n.id === targetId) return here;
    const found = findFuncPath(n.children, targetId, here);
    if (found) return found;
  }
  return null;
}

// Flatten function tree into a flat list for path dropdowns.
function flattenFuncTree(nodes, result = []) {
  for (const n of nodes || []) {
    result.push(n);
    flattenFuncTree(n.children, result);
  }
  return result;
}

// Flatten with depth info attached (for same-level-only dropdown filtering).
function flattenFuncTreeWithDepth(nodes, depth = 0, result = []) {
  for (const n of nodes || []) {
    result.push({ ...n, _depth: depth });
    flattenFuncTreeWithDepth(n.children, depth + 1, result);
  }
  return result;
}

// ---------------------------------------------------------------------------
// FuncPathDropdowns — re-parent a function by picking a new parent from the tree.
// Mirrors ProductsView PathDropdowns but scoped to the project function tree.
// disabled=true (outside edit mode) → shows current parent code read-only.
//
// Tree nodes have id = 'func:<numericId>'. row._parentId is numeric (or null = root).
// We extract numeric id via numId(node.id) for option values.
// ---------------------------------------------------------------------------
function numId(nodeId) {
  // nodeId = 'func:3' → 3
  if (!nodeId) return null;
  const parts = String(nodeId).split(':');
  return parts.length > 1 ? Number(parts[1]) : Number(nodeId);
}

// FuncPathDropdowns — pick a new parent for a function, ANYWHERE it is legal.
//
// Alex, 2026-08-09: "We have to do that one now, because I am [b]locked to move
// things around like I would like." He was right, and the block was real rather
// than cosmetic: §6 offered only nodes at the CURRENT PARENT'S depth, and
// reparentFunction refused any move that changed depth at all. So a function could
// slide sideways and nothing else — never promoted, never demoted, never sent to
// the root. The server rule is gone (replaced by a real cycle guard); this is the
// picker catching up.
//
// Now: every function in the project is a candidate except the node itself and its
// own descendants — those are the only ones that would make a loop, and the server
// refuses them too. Options carry the FULL PATH and are indented by depth, because
// a flat list of 192 identically-shaped rows is what made this unusable: several
// functions share a code (01 appears at many parents), so the code alone does not
// say which one you are choosing.
//
// row._depth: 0 = root-level, 1 = child of a root, etc.
// FuncPathDropdowns — re-parent a function using THE cascade, not a flat list.
//
// Alex, 2026-08-09: "It should follow the cascade drop-down list standard that we
// have developed." The one long indented <select> that replaced the same-level
// list was still the wrong SHAPE — every screen picks a path one level at a time,
// and this was the last control that made you find your destination in a wall of
// options instead of walking to it.
//
// Built from the same primitives as ProductsView's PathDropdowns — CascadeSelect
// (two columns: code left, label right) plus useCascadeSignal for rule 4's
// auto-advance. Not a second cascade: the same one, composed for a different
// shape. LevelCascade is fixed at catalog L2 > L3 > leaf over rows; a project
// function tree is N levels deep over NODES, and you select a node at any level,
// so it composes the atom rather than bending a component built for the other job.
//
// TWO THINGS THIS SHAPE HAS THAT A FIXED CASCADE DOES NOT:
//   • the depth is not known in advance — one level is rendered per step you have
//     taken, plus one more whenever the node you landed on has children to offer,
//     so the control grows as you descend and stops when there is nowhere deeper.
//   • ROOT is a destination. It is the placeholder on the first level, so clearing
//     level 1 means "top level" rather than "nothing chosen".
//
// The node's own subtree never appears: those are the only illegal targets, and
// reparentFunction refuses them too, so the picker cannot offer a move the server
// would reject.
function funcNodeById(nodes, id) {
  for (const n of nodes || []) {
    if (numId(n.id) === id) return n;
    const hit = funcNodeById(n.children, id);
    if (hit) return hit;
  }
  return null;
}

function FuncPathDropdowns({ row, tree, onMove, disabled, onNavigate, stagedParentId }) {
  const cascade = useCascadeSignal();

  // The ancestor chain of the CURRENT parent, root-first. This is what the levels
  // are seeded from, so an unopened cell already shows where the function sits.
  const seed = useMemo(() => {
    const pid = stagedParentId !== undefined ? stagedParentId : row._parentId;
    if (pid == null) return [];
    const path = findFuncPath(tree, `func:${pid}`) || [];
    return path.map(n => numId(n.id));
  }, [tree, row._parentId, stagedParentId]);

  const [chain, setChain] = useState(seed);
  // Re-seed on entering AND leaving edit mode, and whenever the row actually
  // moves — the same fix PathDropdowns needed: drafts live here, the component is
  // not remounted when edit mode ends, so a cancelled pick would otherwise stay on
  // screen and the cell would describe a move that never happened.
  const seedKey = seed.join('/');
  useEffect(() => { setChain(seed); }, [disabled, seedKey]);

  const banned = useMemo(() => {
    const out = new Set([row.id]);
    const self = funcNodeById(tree, row.id);
    (function descend(kids) {
      for (const k of kids || []) { out.add(numId(k.id)); descend(k.children); }
    })(self?.children);
    return out;
  }, [tree, row.id]);

  const optionsAt = level => {
    const parentId = level === 0 ? null : chain[level - 1];
    const kids = level === 0 ? tree : (funcNodeById(tree, parentId)?.children || []);
    return (kids || []).filter(n => !banned.has(numId(n.id)));
  };

  if (disabled) {
    // Rule: read mode shows the PATH, via the shared crumbs — the same thing the
    // product hierarchy shows, instead of one bare code that never said which of
    // the many "01"s you were looking at.
    const parts = seed.map(id => {
      const n = funcNodeById(tree, id);
      return { label: n?.code || `#${id}`, nodeId: n ? n.id : null };
    });
    if (!parts.length) parts.push({ label: '—', nodeId: null });
    const crumbs = parts.map(p => ({ label: p.label, onClick: (onNavigate && p.nodeId != null) ? () => onNavigate(p.nodeId) : undefined }));
    return <Breadcrumb dense crumbs={crumbs} />;
  }

  const toOptions = nodes => nodes.map(n => ({
    code: numId(n.id), display: n.code || '', label: n.label || '',
  }));

  const levels = [];
  for (let i = 0; i <= chain.length; i++) {
    const opts = optionsAt(i);
    // Stop offering a deeper level once there is nothing legal beneath the last
    // pick — an empty dropdown is not a choice, and drawing one implies otherwise.
    if (i > 0 && !opts.length) break;
    levels.push({
      ariaLabel: i === 0 ? 'Parent level 1' : `Parent level ${i + 1}`,
      width: 150,
      allowClear: i === 0,
      placeholder: i === 0 ? '— root (top level) —' : '— deeper —',
      value: chain[i] ?? null,
      options: toOptions(opts),
      openSignal: i === 0 ? undefined : cascade.signalFor(`l${i}`),
      onChange: v => {
        const id = v === '' || v == null ? null : Number(v);
        if (id == null) { setChain([]); onMove(null); return; }   // root
        setChain([...chain.slice(0, i), id]);
        onMove(id);
        cascade.advance(`l${i + 1}`);   // rule 4 — a pick opens the next level
      },
    });
  }

  return <CascadeSelect levels={levels} gap={3} />;
}

// ---------------------------------------------------------------------------
// Project list columns
// ---------------------------------------------------------------------------
// §8: Total removed from project list — per-row total computation is slow;
// project total is shown in the interior header. Glyph unchanged.
const PROJECT_COLS = [
  // COS-5 (Alex, 2026-10-15: "OK for the picture drop") — the project's logo: drop a
  // picture on the cell, or click it to choose one. Stored as item_images['proj:<id>'].
  {
    key: '_logo', label: 'Logo', width: 110,
    render: row => <PictureDrop dbId={`proj:${row.id}`} what="logo" natural maxWidth={96} />,
  },
  {
    key: 'code', label: 'Code', width: 110, addField: true, editable: true, textKind: 'code',
    render: row => <Text kind="code">{row.code}</Text>,
  },
  { key: 'customer', label: 'Customer', width: 240, addField: true, editable: true },
  {
    key: 'status', label: 'Status', width: 120,
    editType: 'select', options: Object.keys(PROJ_META),
    render: row => <Badge meta={PROJ_META[row.status]} />,
  },
];

// ---------------------------------------------------------------------------
// ProjectsView
// ---------------------------------------------------------------------------
// externalTarget (WU3 — Where-used deep link, App-owned): { projectId, functionId,
// placementId, token }. `token` must change each dispatch (even for a repeat target)
// so the effect below re-fires. Additive only — does not alter any existing
// internal navigation/selection state shape.
// UnchargedTag — "⚠ N hr not charged" beside a project TOTAL (Alex, 2026-10-15: the
// labour warning "carry over to each total. It will be impossible to miss").
// Hours a placement's product carries but no labour line charges, rolled up exactly
// like the money (qty-weighted, project-db.js funcContribution). Nothing when zero.
// compact: for a narrow grid cell — its own line under the money, glyph and hours
// only (the words go in the title), so it never clips the total beside it.
function UnchargedTag({ hours, compact = false }) {
  if (!(hours > 0)) return null;
  const words = `${formatQty(hours)} hr not charged: a placed product has no labour line`;
  return (
    <span title={words} style={{
      ...(compact ? { display: 'block' } : { marginLeft: 6 }),
      color: 'var(--th-status-deprecated)', fontWeight: 400, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
    }}>
      {WARN} {formatQty(hours)} hr{compact ? '' : ' not charged'}
    </span>
  );
}

export default function ProjectsView({ view, setView, externalTarget, workingIn = '', onNavigateToProduct, onNavigateToPlacement, onOpenProject } = {}) {
  // §7: skip clearing selectedFuncId on the very first mount (it was restored from localStorage)
  const isFirstMount = useRef(true);

  // -- State -----------------------------------------------------------------
  // ERR-1 — action failures render in the app, next to the work, instead of in a
  // browser popup. Follows the house rule already stated in HistoryPanel.jsx:
  // "never window.confirm/alert".
  const [actionError, setActionError] = useState(null);
  const [projects, setProjects] = useState([]);
  // §7: restore currentProjectId from localStorage on mount
  const [currentProjectId, setCurrentProjectId] = useState(() => {
    try { return localStorage.getItem('inoviu:projectsCurrentId') || null; } catch { return null; }
  });
  const [selectedProjectRowId, setSelectedProjectRowId] = useState(null); // §1: list screen highlight
  const [editingProjectRowId, setEditingProjectRowId] = useState(null);   // G1/CRUD: list row edit (✎)
  const [project, setProject] = useState(null);
  // §7: restore selectedFuncId for the current project
  const [selectedFuncId, setSelectedFuncId] = useState(() => {
    try {
      const pid = localStorage.getItem('inoviu:projectsCurrentId');
      return pid ? (localStorage.getItem(`inoviu:projectsFuncId:${pid}`) || null) : null;
    } catch { return null; }
  });
  const [placements, setPlacements] = useState([]);
  const [pickable, setPickable] = useState([]);
  const [addProductId, setAddProductId] = useState('');
  const [saving, setSaving] = useState(false);
  // Item 2b (v28) — L1→L2→L3→Product cascade for the placement picker, replacing
  // the flat grouped <select> over `pickable`. Fetched once from /api/products/tree
  // (same shape ProductsView uses); the leaf dropdown is filtered to `pickable` ids.
  const [productTree, setProductTree] = useState([]);
  const [placeFilters, setPlaceFilters] = useState({ l1: '', l2: '', l3: '' });

  // Function grid state (mirrors ProductsView hierarchy grid).
  const [funcGridRows, setFuncGridRows] = useState([]);
  const [editingFuncRowId, setEditingFuncRowId] = useState(null);
  // Separate from the grid's editRowId on purpose: editing the function's own row
  // must not also put one of its children into edit mode.
  const [editingFuncHeaderRowId, setEditingFuncHeaderRowId] = useState(null);
  const [selectedFuncRowId, setSelectedFuncRowId] = useState(null); // §1: single-click highlight

  // Placement selection. UX-17.8 retired the right-hand ParameterPanel this used to
  // feed: configuring happens in the expanded row, and selection now only drives the
  // cost breakdown in PlacementSidePanel (server-resolved, no product fetch).
  const [selectedPlacementId, setSelectedPlacementId] = useState(null);
  const [editingPlacementRowId, setEditingPlacementRowId] = useState(null); // G1/CRUD: rename via ✎

  // UX-16 — which placement rows have their configuration expanded. Every row's
  // CONFIGURABLE ONLY, on this screen (Alex, 2026-08-04: "In Function, Parameters that
  // are not configurable should not show up"). The server deliberately RETURNS internal
  // params -- they are worth seeing when chasing a number on the Product screen, where
  // you are building the thing. Here you are CONFIGURING a placement, and an internal
  // param is by definition not something you can set, so it is six rows of noise around
  // the two that are real choices. Filtered in the view, not the API: the same payload
  // still feeds the Product screen, which wants them.
  const configurableOf = (resolved) => (resolved?.params || []).filter(p => p.configurable !== false);

  // parameters already arrive with the placements list (resolved.params), so
  // "expand all" is free: no fetch per row.
  const [expandedPlacementIds, setExpandedPlacementIds] = useState(() => new Set());
  // UX-17.8 — per-row edit buffers, { [placementId]: { [paramName]: value } }. One
  // buffer per row, not one panel-wide set: expand-all means many rows can be open,
  // and each has to stay independently dirty/saveable.
  const [placementEdits, setPlacementEdits] = useState({});
  const [savingPlacementId, setSavingPlacementId] = useState(null);
  const togglePlacementExpanded = useCallback((id) => {
    setExpandedPlacementIds(prev => {
      const next = new Set(prev);
      const k = String(id);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }, []);

  // S2.1 Fix 5 — resolved size_lists options per standard size_unit (value=size_id,
  // label=look, nps), for the placement panel's size-unit LIST param dropdown. Fetched
  // lazily and cached by list_name (mirrors ProductsView's fetchSizeListValues). The
  // panel stores the picked size's NUMERIC nps as the override, so a bound comp size
  // re-resolves through the same env path as the typical (client==server).
  const [sizeListOptionsByUnit, setSizeListOptionsByUnit] = useState({});
  const _sizeListFetched = useRef(new Set());
  const fetchSizeListOptions = useCallback((name) => {
    if (!name || name === 'custom' || _sizeListFetched.current.has(name)) return;
    _sizeListFetched.current.add(name);
    fetch(`/api/products/options/size-list-values?name=${encodeURIComponent(name)}`)
      .then(r => r.json())
      .then(opts => setSizeListOptionsByUnit(p => ({ ...p, [name]: Array.isArray(opts) ? opts : [] })))
      .catch(err => { console.error(err); _sizeListFetched.current.delete(name); });
  }, []);
  // Prefetch options for every standard size_unit list param across ALL placements in the
  // section — UX-17.8 put the size dropdown in the row itself, so any row can need them,
  // not just a selected one. Still one fetch per distinct list_name, cached by name.
  useEffect(() => {
    const units = new Set();
    for (const p of placements) {
      for (const par of configurableOf(p.resolved)) {
        if (par.type === 'list' && par.size_unit && par.size_unit !== 'custom') units.add(par.size_unit);
      }
    }
    units.forEach(fetchSizeListOptions);
  }, [placements, fetchSizeListOptions]);

  // WORKING IN (INF-8, Alex 2026-09-25) — opening a project makes it the top
  // bar's Working-in project, so what you add next lands in the project you are
  // looking at. Since D9 the bar drives this screen too (the effect further down);
  // the two agree because each only acts when they differ.
  // Returning to the list leaves the bar alone — viewing the list is not working.
  useEffect(() => {
    if (currentProjectId) onOpenProject?.(currentProjectId);
  }, [currentProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // §7: persist currentProjectId to localStorage
  useEffect(() => {
    try {
      if (currentProjectId) localStorage.setItem('inoviu:projectsCurrentId', String(currentProjectId));
      else localStorage.removeItem('inoviu:projectsCurrentId');
    } catch {}
  }, [currentProjectId]);

  // §7: persist selectedFuncId scoped to the current project
  useEffect(() => {
    if (!currentProjectId) return;
    try {
      if (selectedFuncId) localStorage.setItem(`inoviu:projectsFuncId:${currentProjectId}`, selectedFuncId);
      else localStorage.removeItem(`inoviu:projectsFuncId:${currentProjectId}`);
    } catch {}
  }, [currentProjectId, selectedFuncId]);

  // -- Data loading ----------------------------------------------------------
  const loadProjects = useCallback(() => {
    fetch('/api/projects').then(r => r.json()).then(rows => setProjects(Array.isArray(rows) ? rows : [])).catch(console.error);
  }, []);

  const loadProject = useCallback((id) => {
    fetch(`/api/projects/${id}`).then(r => r.json()).then(setProject).catch(console.error);
  }, []);

  const loadPlacements = useCallback((funcId) => {
    if (!funcId) { setPlacements([]); return; }
    const numId = funcId.split(':')[1];
    fetch(`/api/functions/${numId}/placements`).then(r => r.json())
      .then(rows => setPlacements(Array.isArray(rows) ? rows : [])).catch(console.error);
  }, []);

  // Build funcGridRows from the project's function tree for the selected parent context.
  // Shows children of the selectedFuncId (or root children when nothing is selected).
  const buildFuncGridRows = useCallback((functions, parentFuncId) => {
    const flat = flattenFuncTree(functions);
    const flatWithDepth = flattenFuncTreeWithDepth(functions);
    // If a function is selected, show its children; else show root-level functions.
    let children;
    if (parentFuncId) {
      const parent = flat.find(n => n.id === parentFuncId);
      children = parent ? (parent.children || []) : [];
    } else {
      children = functions;
    }
    return children.map((f, i) => {
      const nId = Number(f.id.split(':')[1]);
      // Find parent numeric id from flat tree (for the path dropdown).
      const parentNode = flat.find(n => (n.children || []).some(c => c.id === f.id));
      const _parentId = parentNode ? Number(parentNode.id.split(':')[1]) : null;
      // §6: depth for same-level-only move enforcement
      const depthNode = flatWithDepth.find(n => n.id === f.id);
      const _depth = depthNode ? depthNode._depth : 0;
      // Count direct placements (non-empty check for delete warning).
      const hasChildren = (f.children || []).length > 0;
      return {
        id: nId,
        _funcNodeId: f.id,
        code: f.code || '',
        label: f.label,
        qty: f.qty ?? 1,
        total: f.total,
        uncharged_hours: f.uncharged_hours ?? 0,
        sort_order: i,
        _pos: i + 1,
        _parentId,
        _depth,
        _hasChildren: hasChildren,
      };
    });
  }, []);

  // Mount: project list + every product + the products tree (for the picker cascade).
  // ⚠ NOT /api/products/placeable — that flag means "finished, a customer can have it"
  // and is the CUSTOMER side's filter. Internally you place whatever you are estimating.
  useEffect(() => { loadProjects(); }, [loadProjects]);
  // Per scope (D9 "Standard + Own"): the server narrows both to Standard + the
  // working project, so the picker is refetched when Working in changes.
  useEffect(() => {
    fetch('/api/products/flat').then(r => r.json()).then(rows => setPickable(Array.isArray(rows) ? rows : [])).catch(console.error);
    fetch('/api/products/tree').then(r => r.json()).then(t => setProductTree(Array.isArray(t) ? t : [])).catch(console.error);
  }, [workingIn]);

  // Enter / leave a project.
  useEffect(() => {
    // §7: on first mount, selectedFuncId is already restored from localStorage — don't reset it.
    if (isFirstMount.current) {
      isFirstMount.current = false;
    } else {
      setSelectedFuncId(null);
    }
    setPlacements([]);
    setEditingFuncRowId(null);
    setSelectedFuncRowId(null);
    if (currentProjectId) loadProject(currentProjectId);
    else { setProject(null); loadProjects(); }
  }, [currentProjectId, loadProject, loadProjects]);

  // Rebuild func grid when project loads or selectedFuncId changes.
  useEffect(() => {
    if (!project) { setFuncGridRows([]); return; }
    setFuncGridRows(buildFuncGridRows(project.functions || [], selectedFuncId));
    setSelectedFuncRowId(null); // §1: clear selection when grid context changes
  }, [project, selectedFuncId, buildFuncGridRows]);

  // Load placements when the selected function changes.
  useEffect(() => { loadPlacements(selectedFuncId); }, [selectedFuncId, loadPlacements]);

  // D9 (Alex 2026-10-14) — WORKING IN DRIVES THIS SCREEN. The bar is the first pick, so
  // the project it names is the one Functions opens. App clears any pending
  // externalTarget when the bar is changed by hand, which is what used to make this
  // direction snap back. Declared BEFORE the externalTarget effect: on a deep link's
  // first render both fire, and the link's project must be the one that wins.
  // On Standard there is no project to open: the screen is the project list.
  useEffect(() => {
    if (!workingIn) { if (currentProjectId) setCurrentProjectId(null); return; }
    if (String(currentProjectId) !== String(workingIn)) setCurrentProjectId(String(workingIn));
  }, [workingIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // WU3 — external deep link (from ProductsView's Where-used "Placed in Projects" grid,
  // via App's onNavigateToPlacement). Staged: enter the project, then select the
  // function once its tree is loaded, then select the placement once its grid is loaded.
  useEffect(() => {
    if (!externalTarget?.projectId) return;
    if (String(currentProjectId) !== String(externalTarget.projectId)) {
      setCurrentProjectId(externalTarget.projectId);
    }
  }, [externalTarget, currentProjectId]);

  useEffect(() => {
    if (!externalTarget?.projectId || !externalTarget?.functionId) return;
    if (String(currentProjectId) !== String(externalTarget.projectId)) return;
    if (!project) return;
    const wantFuncId = `func:${externalTarget.functionId}`;
    if (selectedFuncId !== wantFuncId) setSelectedFuncId(wantFuncId);
  }, [externalTarget, currentProjectId, project, selectedFuncId]);

  const refreshProject = useCallback(() => {
    if (currentProjectId) loadProject(currentProjectId);
    loadPlacements(selectedFuncId);
  }, [currentProjectId, selectedFuncId, loadProject, loadPlacements]);

  // -- P1: placement configuration ------------------------------------------
  const selectedPlacement = useMemo(
    () => placements.find(p => p.id === selectedPlacementId) || null,
    [placements, selectedPlacementId],
  );
  // Clear placement selection whenever the function (and thus the placement list) changes.
  useEffect(() => {
    setSelectedPlacementId(null);
    setEditingPlacementRowId(null);
    setExpandedPlacementIds(new Set());
    setPlacementEdits({});
  }, [selectedFuncId]);

  // Select a placement row — highlight + the right-hand cost breakdown. UX-17.8: it no
  // longer fetches the product, because configuring moved INTO the row and the
  // breakdown comes from the server-resolved `resolved` already on the row.
  const handleSelectPlacement = useCallback((row) => {
    setSelectedPlacementId(prev => (prev === row.id ? null : row.id));
  }, []);

  // WU3 (cont'd) — final stage of the external deep link: once the target function's
  // placements have loaded, select the target placement (reuses handleSelectPlacement,
  // same as a manual row click — no separate code path).
  useEffect(() => {
    if (!externalTarget?.placementId) return;
    if (selectedFuncId !== `func:${externalTarget.functionId}`) return;
    const row = placements.find(p => p.id === externalTarget.placementId);
    if (row && selectedPlacementId !== row.id) handleSelectPlacement(row);
  }, [externalTarget, selectedFuncId, placements, selectedPlacementId, handleSelectPlacement]);

  // UX-17.8 — edit one parameter of one row. The buffer holds ONLY what changed;
  // everything else falls back to the server-resolved value in force.
  const handleRowParamChange = useCallback((rowId, name, val) => {
    setPlacementEdits(prev => ({ ...prev, [rowId]: { ...(prev[rowId] || {}), [name]: val } }));
  }, []);

  const handleRowRevert = useCallback((rowId) => {
    setPlacementEdits(prev => { const next = { ...prev }; delete next[rowId]; return next; });
  }, []);

  // Save = the existing placement-save path (PATCH /api/placements/:id with actual_values).
  // MERGED over the row's stored actual_values, never replacing it: that JSON also carries
  // the importer's `_src_*` provenance, which is the only trace of where the placement came
  // from (see BACKLOG UX-16). Sending just the edited params would silently erase it.
  const handleRowSave = useCallback(async (row) => {
    const edits = placementEdits[row.id];
    if (!edits || savingPlacementId != null) return;
    setSavingPlacementId(row.id);
    try {
      await fetch(`/api/placements/${row.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actual_values: { ...(row.actual_values || {}), ...edits } }),
      });
      handleRowRevert(row.id);
      refreshProject();
    } catch (err) { console.error(err); } finally { setSavingPlacementId(null); }
  }, [placementEdits, savingPlacementId, refreshProject, handleRowRevert]);

  // -- Project list actions --------------------------------------------------
  const createProject = useCallback(async (fields) => {
    const res = await fetch('/api/projects', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: fields.code, customer: fields.customer }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    loadProjects();
  }, [loadProjects]);

  // G1/CRUD: inline edit of a project row (code / customer / status) — the list
  // previously had no update path at all (create/delete only).
  const handleSaveProjectRow = useCallback(async (rowId, field, value) => {
    const res = await fetch(`/api/projects/${rowId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Save failed' })); setActionError(e.error); return; }
    loadProjects();
  }, [loadProjects]);

  // Copy a project (PROJECTS_AND_TEMPLATES.md step 3): a template is just a project,
  // so a new job starts as a copy of one. The copy lands as <code>-COPY; rename inline.
  const handleCopyProject = useCallback(async (row) => {
    const res = await fetch(`/api/projects/${row.id}/copy`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Copy failed' })); setActionError(e.error || 'Copy failed'); return; }
    loadProjects();
  }, [loadProjects]);

  // Empty-guard pre-check (mirrors the catalog/function grids): warn for non-empty
  // projects instead of confirming. Server is the authority and rejects too.
  const handleProjectDeleteCheck = useCallback((row) => {
    if ((row.func_count || 0) > 0) return "Can't delete — remove its functions first";
    return null;
  }, []);

  const handleDeleteProject = useCallback(async (row) => {
    const res = await fetch(`/api/projects/${row.id}`, { method: 'DELETE' });
    if (!res.ok) {
      const e = await res.json().catch(() => ({ error: 'Delete failed' }));
      setActionError(e.error || 'Delete failed');
      return;
    }
    if (String(currentProjectId) === String(row.id)) setCurrentProjectId(null);
    loadProjects();
  }, [currentProjectId, loadProjects]);

  // -- Function grid actions (mirrors ProductsView hierarchy grid) -----------

  // DataGrid built-in onAdd (fields = { code, label } from the addField columns).
  // Adds under the currently-selected function (its children) or at root.
  // Throws on error so DataGrid surfaces the message and keeps the add row open.
  const handleAddFunc = useCallback(async (fields) => {
    const label = (fields.label || '').trim();
    if (!label || !currentProjectId) throw new Error('Description is required');
    const parent_id = selectedFuncId ? Number(selectedFuncId.split(':')[1]) : null;
    const res = await fetch(`/api/projects/${currentProjectId}/functions`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label, code: (fields.code || '').trim(), parent_id }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    loadProject(currentProjectId);
  }, [currentProjectId, selectedFuncId, loadProject]);

  const handleSaveFuncRow = useCallback(async (rowId, field, value) => {
    // '_parent' is the cascade's staged pick, not a column on the row. It goes to
    // the guarded /reparent route rather than a plain PATCH, because that is where
    // the cycle and cross-project checks live — a parent change must never take a
    // path that skips them.
    if (field === '_parent') {
      const r = await fetch(`/api/functions/${rowId}/reparent`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parent_id: value === '' || value == null ? null : Number(value) }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || 'Could not move this function.'); }
      loadProject(currentProjectId);
      return;
    }
    const res = await fetch(`/api/functions/${rowId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value }),
    });
    if (!res.ok) { const e = await res.json(); setActionError(e.error); return; }
    loadProject(currentProjectId);
  }, [currentProjectId, loadProject]);

  const handleReorderFunc = useCallback(async (rowId, targetIndex) => {
    const res = await fetch(`/api/functions/${rowId}/move`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ toIndex: targetIndex }),
    });
    if (!res.ok) return;
    loadProject(currentProjectId);
  }, [currentProjectId, loadProject]);


  // STANDARD (Alex 2026-07-02): guard on ✕ BEFORE the confirm — children (sync)
  // then placements (async), mirroring the server deleteFunction guards.
  const handleFuncDeleteCheck = useCallback((row) => {
    if (row._hasChildren) return "Can't delete — remove child functions first";
    return fetch(`/api/functions/${row.id}/placements`)
      .then(r => r.ok ? r.json() : [])
      .then(pl => pl.length ? `Can't delete — has ${pl.length} placement(s); remove them first.` : null)
      .catch(() => null);
  }, []);

  const handleDeleteFunc = useCallback(async (row) => {
    const numId = row.id;
    const res = await fetch(`/api/functions/${numId}`, { method: 'DELETE' });
    if (!res.ok) {
      const e = await res.json().catch(() => ({ error: 'Delete failed' }));
      setActionError(e.error || 'Delete failed');
      return;
    }
    if (selectedFuncId === `func:${numId}`) setSelectedFuncId(null);
    loadProject(currentProjectId);
  }, [currentProjectId, selectedFuncId, loadProject]);

  // C1 — deep-copy a function (subtree + placements); refresh reveals the new
  // sibling (labeled "<original> copy") at the end of the current list.
  const handleCopyFunc = useCallback(async (row) => {
    try {
      const res = await fetch(`/api/functions/${row.id}/copy`, { method: 'POST' });
      if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Copy failed' })); setActionError(e.error || 'Copy failed'); return; }
      loadProject(currentProjectId);
    } catch (err) { console.error(err); }
  }, [currentProjectId, loadProject]);

  // -- Function grid column definition (mirrors hierarchyCols in ProductsView) --
  const funcTree = project?.functions || [];
  const funcCols = useMemo(() => {
    // §6: use depth-annotated flat list so FuncPathDropdowns can filter same-level-only targets
    return [
      {
        // Parent column — shows current parent in read mode; same-level dropdown in edit mode (§6).
        key: '_parent', label: 'Parent', width: 150,
        render: (row, ctx) => {
          const inEdit = editingFuncRowId != null && String(row.id) === String(editingFuncRowId);
          const bufd = ctx?.buffered || {};
          return (
            <FuncPathDropdowns
              key={`${row.id}-parent-${row._parentId}-${row._depth}`}
              row={row}
              tree={funcTree}
              stagedParentId={'_parent' in bufd ? bufd._parent : undefined}
              onMove={newParentId => ctx?.buffer?.(row.id, '_parent', newParentId)}
              disabled={!inEdit}
            />
          );
        },
      },
      {
        key: 'code', label: 'Tag', width: 90,
        editable: true, addField: true, textKind: 'code',
        render: row => (
          <Text kind="code">
            {row.code || <span style={{ color: 'var(--th-border-light)' }}>—</span>}
          </Text>
        ),
      },
      { key: 'label', label: 'Label', width: 240, editable: true, addField: true },
      {
        key: 'qty', label: 'Qty', width: 55, align: 'right', editable: true, textKind: 'small',
        render: row => (
          <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
            {row.qty}
          </Text>
        ),
      },
    ];
  }, [funcTree, editingFuncRowId]);

  // -- Placement actions -----------------------------------------------------
  // Returns true on success / false on failure so the renderAddForm ghost row
  // (P5) knows whether to close itself — mirrors handleCompFormSave's contract.

  // G1/CRUD: rename a placement inline (label was previously not editable anywhere,
  // though PATCH /api/placements/:id already supported it).
  const handleSavePlacementRow = useCallback(async (rowId, field, value) => {
    if (field !== 'label') return;
    const res = await fetch(`/api/placements/${rowId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: value }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Save failed' })); setActionError(e.error); return; }
    loadPlacements(selectedFuncId);
  }, [selectedFuncId, loadPlacements]);

  const removePlacement = useCallback(async (row) => {
    const res = await fetch(`/api/placements/${row.id}`, { method: 'DELETE' });
    if (!res.ok) { const e = await res.json(); setActionError(e.error); return; }
    refreshProject();
  }, [refreshProject]);

  const moveRow = useCallback(async (id, targetIndex) => {
    const res = await fetch(`/api/placements/${id}/move`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ toIndex: targetIndex }),
    });
    if (!res.ok) { const e = await res.json(); setActionError(e.error); return; }
    loadPlacements(selectedFuncId);
  }, [selectedFuncId, loadPlacements]);

  // UX-17.5 — duplicate a placement (the copy icon). The server lands it directly after its
  // source and drops the importer's provenance (see copyPlacement).
  const handleCopyPlacement = useCallback(async (row) => {
    const res = await fetch(`/api/placements/${row.id}/copy`, { method: 'POST' });
    if (!res.ok) { const e = await res.json().catch(() => ({})); setActionError(e.error || 'Copy failed'); return; }
    refreshProject();
  }, [refreshProject]);

  // UX-17.3 — swap an existing placement's product from the row's edit mode. The
  // server owns the carry-over rule (same name AND same type survives); the UI only
  // has to say which product, and to warn that the rest goes.
  const handleSwapProduct = useCallback(async (row, productId) => {
    if (!productId || String(productId) === String(row.product_id)) return;
    const res = await fetch(`/api/placements/${row.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_id: Number(productId) }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); setActionError(e.error || 'Swap failed'); return; }
    setEditingPlacementRowId(null);
    refreshProject();
  }, [refreshProject]);

  // Item 2b (v28) — placement picker cascade over the PRODUCTS tree, filtered to
  // `pickable` ids only (a Set, for O(1) lookup). Mirrors CompEditSubRow's
  // Product-source cascade in ProductsView (same tree shape: c1-/c2-/c3-
  // category nodes + product leaves flagged isProduct).
  const pickableIds = useMemo(() => new Set(pickable.map(p => String(p.id))), [pickable]);
  const placeL1Node = placeFilters.l1 ? productTree.find(n => n.code === placeFilters.l1) : null;
  const placeL2Opts = placeL1Node ? (placeL1Node.children || []).filter(c => !c.isProduct) : [];
  const placeL2Node = placeFilters.l2 ? placeL2Opts.find(n => n.code === placeFilters.l2) : null;
  const placeL3Opts = placeL2Node ? (placeL2Node.children || []).filter(c => !c.isProduct) : [];
  const placeL3Node = placeFilters.l3 ? placeL3Opts.find(n => n.code === placeFilters.l3) : null;
  const placeLeaves = placeL3Node
    ? (placeL3Node.children || []).filter(c => c.isProduct && pickableIds.has(String(c._productId)))
    : [];
  const onPlaceFilterChange = (level, value) => {
    setPlaceFilters(prev => {
      const next = level === 'l1' ? { l1: value, l2: '', l3: '' }
        : level === 'l2' ? { ...prev, l2: value, l3: '' }
        : { ...prev, l3: value };
      return next;
    });
    setAddProductId('');
  };

  // -- UX-17.3 (2nd pass) — the SWAP cascade, in the Source·Ref cell ----------
  // Alex, on the flat 189-entry dropdown I shipped first: "you did it the old way.
  // You did not use the auto cascade dropdown." So this is the same L1▸L2▸L3▸product
  // cascade the add form uses AND the same one the product screen puts in its
  // Source·Ref cell — with openSignal wired, because a cascade auto-advances.
  const [swapFilters, setSwapFilters] = useState({ l1: '', l2: '', l3: '' });
  // 2026-08-04: per-level counters replaced by the shared hook, same as
  // PathDropdowns -- see useCascadeSignal for why always-a-number is the risky style.
  // The l1->l2->l3->leaf chain stays here: the ORDER is this cascade's business.
  const swapCascade = useCascadeSignal();
  // UX-3 (2026-08-04) -- the "Place into" cascade had no auto-advance at all: none of
  // its four controls passed openSignal, so placing a product meant opening every
  // level by hand. Same chain shape as the swap cascade beside it.
  const placeCascade = useCascadeSignal();
  const advancePlace = (from) => {
    const nextOf = { l1: 'l2', l2: 'l3', l3: 'leaf' };
    const k = nextOf[from];
    if (k) placeCascade.advance(k);
  };
  const swapSig = { l1: swapCascade.signalFor('l1'), l2: swapCascade.signalFor('l2'), l3: swapCascade.signalFor('l3'), leaf: swapCascade.signalFor('leaf') };
  const advanceSwap = useCallback((from) => {
    const nextOf = { l1: 'l2', l2: 'l3', l3: 'leaf' };
    const k = nextOf[from];
    if (k) swapCascade.advance(k);
  }, [swapCascade.advance]);

  // Where a product sits in the products tree, so the cascade OPENS on the product
  // the placement already uses instead of on three empty pickers.
  const productPath = useCallback((productId) => {
    for (const l1 of productTree) {
      for (const l2 of (l1.children || [])) {
        for (const l3 of (l2.children || [])) {
          if ((l3.children || []).some(c => c.isProduct && String(c._productId) === String(productId))) {
            return { l1: l1.code, l2: l2.code, l3: l3.code };
          }
        }
      }
    }
    return { l1: '', l2: '', l3: '' };
  }, [productTree]);

  // Seed (and reset) the cascade whenever a different row enters edit mode.
  useEffect(() => {
    if (editingPlacementRowId == null) { setSwapFilters({ l1: '', l2: '', l3: '' }); return; }
    const row = placements.find(p => String(p.id) === String(editingPlacementRowId));
    setSwapFilters(row ? productPath(row.product_id) : { l1: '', l2: '', l3: '' });
  }, [editingPlacementRowId, placements, productPath]);

  const swapL1Node = swapFilters.l1 ? productTree.find(n => n.code === swapFilters.l1) : null;
  const swapL2Opts = swapL1Node ? (swapL1Node.children || []).filter(c => !c.isProduct) : [];
  const swapL2Node = swapFilters.l2 ? swapL2Opts.find(n => n.code === swapFilters.l2) : null;
  const swapL3Opts = swapL2Node ? (swapL2Node.children || []).filter(c => !c.isProduct) : [];
  const swapL3Node = swapFilters.l3 ? swapL3Opts.find(n => n.code === swapFilters.l3) : null;
  const swapLeaves = swapL3Node
    ? (swapL3Node.children || []).filter(c => c.isProduct && pickableIds.has(String(c._productId)))
    : [];
  const onSwapFilterChange = (level, value) => {
    setSwapFilters(prev => level === 'l1' ? { l1: value, l2: '', l3: '' }
      : level === 'l2' ? { ...prev, l2: value, l3: '' }
      : { ...prev, l3: value });
    if (value) advanceSwap(level);
  };

  const funcPath = useMemo(
    () => (selectedFuncId ? findFuncPath(project?.functions, selectedFuncId) : null),
    [selectedFuncId, project],
  );
  const selectedFunc = funcPath?.[funcPath.length - 1] || null;

  // UX-12 / UX-18 — THE FUNCTION'S OWN ROW.
  //
  // Catalog and Products have led with the item's own editable row since July
  // (HeaderRow); the Function screen never got that pass, so to rename a function
  // you had to go UP a level and edit it in its parent's grid. Alex, on the shape
  // he liked elsewhere: "the main title level row edit as soon as I click and it
  // is cool."
  //
  // Same columns as the grid below it, deliberately: one definition of what a
  // function IS. The only difference is `#`, which is dropped — a position number
  // means something among siblings and nothing about the item itself.
  //
  // Its own editRowId, separate from the grid's, so editing the header does not
  // put a child row into edit mode at the same time.
  const funcHeaderRow = useMemo(() => {
    if (!selectedFunc) return null;
    const parent = (funcPath || [])[(funcPath || []).length - 2] || null;
    return {
      id: numId(selectedFunc.id),
      _funcNodeId: selectedFunc.id,
      code: selectedFunc.code || '',
      label: selectedFunc.label,
      qty: selectedFunc.qty ?? 1,
      total: selectedFunc.total,
      uncharged_hours: selectedFunc.uncharged_hours ?? 0,
      _parentId: parent ? numId(parent.id) : null,
      _depth: (funcPath || []).length - 1,
    };
  }, [selectedFunc, funcPath]);

  const funcHeaderCols = useMemo(() => {
    return [
      {
        key: '_parent', label: 'Parent', width: 150,
        render: (row, ctx) => {
          const inEdit = editingFuncHeaderRowId != null && String(row.id) === String(editingFuncHeaderRowId);
          const bufd = ctx?.buffered || {};
          return (
            <FuncPathDropdowns
              key={`hdr-${row.id}-${row._parentId}-${row._depth}`}
              row={row}
              tree={funcTree}
              stagedParentId={'_parent' in bufd ? bufd._parent : undefined}
              onMove={newParentId => ctx?.buffer?.(row.id, '_parent', newParentId)}
              disabled={!inEdit}
            />
          );
        },
      },
      {
        key: 'code', label: 'Tag', width: 90, editable: true, textKind: 'code',
        render: row => (
          <Text kind="code">
            {row.code || <span style={{ color: 'var(--th-border-light)' }}>—</span>}
          </Text>
        ),
      },
      { key: 'label', label: 'Description', width: 240, editable: true },
      {
        key: 'qty', label: 'Qty', width: 55, align: 'right', editable: true, textKind: 'small',
        render: row => (
          <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{row.qty}</Text>
        ),
      },
      {
        // Read-only: this function's rolled-up cost. Not editable anywhere, so it
        // renders as text rather than pretending to be a field.
        key: 'total', label: 'Cost', width: 110, align: 'right',
        render: row => (
          <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 'var(--fs-small)', color: NAVY, fontWeight: 600 }}>
            {money(row.total)}<UnchargedTag hours={row.uncharged_hours} compact />
          </span>
        ),
      },
    ];
  }, [funcTree, editingFuncHeaderRowId]);


  // Placement rows (#-first).
  const placementRows = useMemo(
    () => placements.map((p, i) => ({ ...p, _seq: i + 1 })),
    [placements],
  );

  // HOURS BUT NO LABOUR (2026-10-14, Alex: "Yes" to carrying the product warning
  // into projects). Placements whose product's install hours nothing charges.
  const unchargedPlacements = useMemo(() => {
    const hit = placementRows.filter(r => r.resolved?.uncharged_hours > 0);
    const hours = Math.round(hit.reduce((s, r) => s + r.resolved.uncharged_hours, 0) * 10000) / 10000;
    return { count: hit.length, hours };
  }, [placementRows]);

  // Placement columns: #-first, consistent with ProductsView placement grid.
  const placementCols = useMemo(() => [
    // UX-17.2 — "Label" is "Description" everywhere else (the FUNCTIONS grid above,
    // product-cols.jsx); Alex, 2026-08-01: "for consistency, I think it's better to
    // call it description instead of label". Field name unchanged — display only.
    { key: 'label', label: 'Description', width: 150, editable: true, addField: true, textKind: 'small', render: row => <span style={{ fontSize: 'var(--fs-small)', fontWeight: 600, color: 'var(--th-text-body)' }}>{row.label}</span> },
    {
      // UX-17.4 — Description · Source·Ref · Product, matching the comps grid
      // (product-cols.jsx). A placement's source is always a PRODUCT, so the glyph is
      // fixed; splitting the code out of the old combined cell is what "add the column
      // reference" asked for.
      key: 'ref', label: 'Source · Ref', width: 190, textKind: 'small',
      render: row => (
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, fontSize: 'var(--fs-small)' }} title="Product">
          <span aria-hidden="true" style={{ color: NAVY, fontWeight: 700 }}>{PRODUCT}</span>
          <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', color: 'var(--th-text-secondary)' }}>
            {row.product_code || `#${row.product_id}`}
          </span>
          {/* UX-17.9 — open the PRODUCT definition (the typical) this placement
              instances. Same convention the comps grid uses: the ICON navigates,
              never the code (A5). */}
          {onNavigateToProduct && (
            <button
              onClick={e => { e.stopPropagation(); onNavigateToProduct(row.product_id); }}
              title="Open the product definition (typical)"
              style={{ ...BTN_GLYPH, lineHeight: 1 }}
            >{LINK}</button>
          )}
        </span>
      ),
      // UX-17.3 (2nd pass) — the SWAP lives here, in Source·Ref, exactly where the
      // product screen puts its own source picker. Picking a leaf swaps immediately
      // (its own PATCH): the swap rewrites actual_values server-side, which the row
      // buffer has no way to represent.
      editRender: row => (
        <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
          <CascadeSelect
            wrap={false}
            levels={[
              {
                ariaLabel: 'Level 1 category', width: 120,
                value: swapFilters.l1, openSignal: swapSig.l1,
                options: productTree.map(n => ({ code: n.code, label: n.label })),
                onChange: code => onSwapFilterChange('l1', code),
              },
              {
                ariaLabel: 'Level 2 category', width: 120,
                value: swapFilters.l2, disabled: !swapL1Node, openSignal: swapSig.l2,
                options: swapL2Opts.map(n => ({ code: n.code, label: n.label })),
                onChange: code => onSwapFilterChange('l2', code),
              },
              {
                ariaLabel: 'Level 3 category', width: 120,
                value: swapFilters.l3, disabled: !swapL2Node, openSignal: swapSig.l3,
                options: swapL3Opts.map(n => ({ code: n.code, label: n.label })),
                onChange: code => onSwapFilterChange('l3', code),
              },
            ]}
          />
          <CascadeDropdown
            ariaLabel="Product"
            width={200}
            placeholder="— pick a product —"
            allowClear={false}
            disabled={!swapL3Node || !!row.frozen}
            openSignal={swapSig.leaf}
            value={String(row.product_id)}
            options={swapLeaves.map(p => ({ code: String(p._productId), display: p.code, label: p.label }))}
            onChange={code => handleSwapProduct(row, code)}
          />
          {row.frozen && (
            <Text kind="note">purchased — frozen</Text>
          )}
        </div>
      ),
    },
    {
      key: 'product', label: 'Product', width: 200,
      // UX-19 — THE PICKER LIVES IN THE PRODUCT COLUMN. Alex, 2026-08-09, on a
      // screenshot of the old floating form: "Attached something I thought we
      // changed." He had asked before — the same complaint moved the components
      // grid onto the in-cell lane on 08-01 and Parameters on 08-04, and this was
      // the third grid, still laying a label, four dropdowns and a description box
      // left-to-right with no relationship to the headers above them. You picked a
      // product under the Description heading and typed the description under
      // nothing at all.
      //
      // Picking a product also PREFILLS the description cell when it is empty —
      // one control setting two fields, which is what ctx.set is for. It only
      // fills a blank: typing your own name for a placement must never be undone
      // by a later pick.
      renderAddCell: ({ fields, set }) => (
        <div style={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <CascadeSelect
            levels={[
              { ariaLabel: 'Level 1 category', width: 96, value: placeFilters.l1,
                options: productTree.map(n => ({ code: n.code, label: n.label })),
                onChange: code => { onPlaceFilterChange('l1', code); if (code) advancePlace('l1'); } },
              { ariaLabel: 'Level 2 category', width: 96, value: placeFilters.l2, disabled: !placeL1Node,
                openSignal: placeCascade.signalFor('l2'),
                options: placeL2Opts.map(n => ({ code: n.code, label: n.label })),
                onChange: code => { onPlaceFilterChange('l2', code); if (code) advancePlace('l2'); } },
              { ariaLabel: 'Level 3 category', width: 96, value: placeFilters.l3, disabled: !placeL2Node,
                openSignal: placeCascade.signalFor('l3'),
                options: placeL3Opts.map(n => ({ code: n.code, label: n.label })),
                onChange: code => { onPlaceFilterChange('l3', code); if (code) advancePlace('l3'); } },
            ]}
          />
          <CascadeDropdown
            ariaLabel="Placeable product"
            width={150}
            placeholder="— pick a product —"
            allowClear={false}
            disabled={!placeL3Node}
            value={addProductId}
            openSignal={placeCascade.signalFor('leaf')}
            options={placeLeaves.map(pr => ({ code: String(pr._productId), display: pr.code, label: pr.label }))}
            onChange={code => {
              setAddProductId(code);
              if (code && !String(fields.label || '').trim()) {
                const prod = pickable.find(pr => String(pr.id) === code);
                if (prod) set('label', prod.label);
              }
            }}
          />
        </div>
      ),
      render: row => (
        <span style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-body)' }}>
          {row.product_label
            ? <OwnerLabel label={row.product_label} ownerOnly={row.product_owner_code} />
            : <span style={{ color: 'var(--th-text-faint)' }}>—</span>}
        </span>
      ),
    },
    {
      // UX-17.8 — this button IS the expander now: configuring happens in the row
      // below, not in a right-hand panel. A placement whose product declares no
      // parameters gets no control rather than one that opens on nothing.
      key: 'config', label: 'Configuration', width: 130,
      render: row => {
        const n = configurableOf(row.resolved).length + (row.resolved?.orphan_params?.length || 0);
        if (!n) return <Text kind="empty" />;
        const open = expandedPlacementIds.has(String(row.id));
        return (
          <button
            onClick={e => { e.stopPropagation(); togglePlacementExpanded(row.id); }}
            title={open ? 'Close the configuration' : `Configure this placement (${n})`}
            aria-expanded={open}
            style={{
              fontSize: 'var(--fs-small)', padding: 'var(--tg-editbtn-pad)', borderRadius: 'var(--tg-editbtn-radius)',
              border: '1px solid var(--th-border)',
              background: open ? 'var(--th-row-selected)' : 'var(--tg-glyph-bg)',
              color: open ? NAVY : 'var(--tg-code-color-other)',
              fontWeight: open ? 700 : 400,
              cursor: 'pointer',
            }}
          >
            <Chevron open={open} />Configure
          </button>
        );
      },
    },
    {
      key: 'qty', label: 'Qty', width: 60, align: 'right',
      render: () => <Text kind="empty" />,
    },
    { key: 'status', label: 'Status', width: 110, render: row => <Badge meta={PROC_META[row.status]} /> },
    // UX-17.7 — the same breakdown the product screen shows (product-cols.jsx:
    // Material · Hours · Labor). Server-resolved, so the split cannot disagree with
    // the total: material + labor reconciles to Price on all 686 placements.
    {
      key: 'mat_cost', label: 'Supply', width: 90, align: 'right',
      render: row => row.resolved?.total_material
        ? <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{money(row.resolved.total_material)}</Text>
        : <Text kind="empty" />,
    },
    {
      key: 'labor_cost', label: 'Labor', width: 90, align: 'right',
      render: row => row.resolved?.total_labor
        ? <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{money(row.resolved.total_labor)}</Text>
        : <Text kind="empty" />,
    },
    {
      key: 'total', label: 'Cost', width: 100, align: 'right',
      render: row => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
          {money(row.resolved?.total)}<UnchargedTag hours={row.resolved?.uncharged_hours} compact />{row.frozen ? <span title="frozen snapshot" style={{ marginLeft: 4 }}>🔒</span> : null}
        </span>
      ),
    },
    {
      key: 'hours', label: 'Hours', width: 70, align: 'right',
      // Hours its product carries but no labour line charges (resolvePlacement's
      // uncharged_hours) ride UNDER the charged hours, ⚠ + words, so a zero-labour
      // placement never reads as a cheap one. The card's notice above says why.
      render: row => {
        const un = row.resolved?.uncharged_hours > 0 ? row.resolved.uncharged_hours : 0;
        const hrs = row.resolved?.total_hours ? row.resolved.total_hours : null;
        if (hrs == null && !un) return <Text kind="empty" />;
        return (
          <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', fontSize: 'var(--fs-small)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
            {hrs != null && (
              <span title={Math.round(hrs) !== hrs ? String(hrs) : undefined} style={{ color: NAVY }}>{formatQty(hrs)} hr</span>
            )}
            {un > 0 && (
              <span title={`${formatQty(un)} hr not charged: the product has no labour line`}
                    style={{ color: 'var(--th-status-deprecated)', whiteSpace: 'nowrap' }}>{WARN} {formatQty(un)} hr</span>
            )}
          </span>
        );
      },
    },
  ], [selectedPlacementId, expandedPlacementIds, togglePlacementExpanded, handleSwapProduct,
      onNavigateToProduct, productTree, swapFilters, swapSig, swapL1Node, swapL2Opts, swapL2Node, swapL3Opts, swapL3Node, swapLeaves]);

  // UX-16 — every placement with something to show, for the expand-all control.
  const expandablePlacementIds = useMemo(
    () => placementRows
      .filter(r => configurableOf(r.resolved).length + (r.resolved?.orphan_params?.length || 0) > 0)
      .map(r => String(r.id)),
    [placementRows],
  );
  const allPlacementsExpanded = expandablePlacementIds.length > 0
    && expandablePlacementIds.every(id => expandedPlacementIds.has(id));

  // ===========================================================================
  // LIST SCREEN
  // ===========================================================================
  if (!currentProjectId) {
    const selectedProjectRow = projects.find(r => r.id === (editingProjectRowId ?? selectedProjectRowId)) || null;
    return (
      // No project opened yet, so no function tree either. The same shell, with `noTree`:
      // the left rail still hosts the switcher AND still resizes and hides. It was a
      // fixed 240px <aside> with no splitter, so on Standard (where Functions opens this
      // list) the pane could not be adjusted (Alex, 2026-10-20).
      <HierarchyWorkspace
        noTree
        treeNodes={[]}
        selectedNodeId={null}
        onSelectNode={() => {}}
        treeHeader={<ViewSwitcher view={view} setView={setView} />}
        storageKey="projects"
        navWidthStorageKey="inoviu:projectsNavWidth"
        defaultNavWidth={240}
        mainStyle={{ padding: PANE_GAP }}
      >
        {/* Cards, like every other screen (Alex, 2026-10-20): Active Projects is the
            list; History and Archive are placeholders until Freeze and Archive exist. */}
        <Card storageKey="projects:section:active" title="Active Projects" count={projects.length}>
          {/* ERR-1 — action failures appear here, in the app, not in a popup. */}
          <Notice onDismiss={() => setActionError(null)}>{actionError}</Notice>
          <DataGrid
            rows={projects}
            columns={PROJECT_COLS}
            onRowClick={row => setSelectedProjectRowId(prev => prev === row.id ? null : row.id)}
            onRowDoubleClick={row => { setCurrentProjectId(row.id); setSelectedProjectRowId(null); setEditingProjectRowId(null); }}
            selectedRowId={selectedProjectRowId}
            onSave={handleSaveProjectRow}
            onEditRow={setEditingProjectRowId}
            editRowId={editingProjectRowId}
            // Auto-edit (2026-08-22, Alex: "switch everything to auto edit") — single
            // click here only ever toggled a bare highlight; opening a project is on
            // double-click, unaffected.
            editOnRowClick
            onAdd={createProject}
            onCopy={handleCopyProject}
            onDelete={handleDeleteProject}
            onDeleteCheck={handleProjectDeleteCheck}
            emptyMessage="No projects yet — add one below."
            storageKey="project-list"
          />
        </Card>
        <Card storageKey="projects:section:history" title="History">
          <Text kind="note">
            {selectedProjectRow
              ? `Frozen snapshots of ${selectedProjectRow.code} will be listed here. Freezing a project is not built yet.`
              : 'Select a project above to see its frozen snapshots. Freezing a project is not built yet.'}
          </Text>
        </Card>
        <Card storageKey="projects:section:archive" title="Archive">
          <Text kind="note">Archived projects will be listed here. Archiving is not built yet.</Text>
        </Card>
      </HierarchyWorkspace>
    );
  }

  // ===========================================================================
  // PROJECT INTERIOR
  // ===========================================================================
  // ALL PROJECTS (2026-10-12) — the way back to the list. DS-5 (08-21) removed the
  // breadcrumb that was the only route there, so once a project was open the list
  // (and its ⧉ copy) could not be reached again.
  const backToList = () => { setCurrentProjectId(null); setSelectedFuncId(null); setEditingFuncRowId(null); };
  const projectTotalNode = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 'var(--fs-small)', fontWeight: 400, textTransform: 'none', color: 'var(--th-text-secondary)' }}>
      {project && <PictureDrop dbId={`proj:${project.id}`} size={22} what="logo" natural maxWidth={90} />}
      <span>
        Project total:{' '}
        <strong style={{ color: NAVY, fontVariantNumeric: 'tabular-nums' }}>{money(project?.total)}</strong>
        <UnchargedTag hours={project?.uncharged_hours} />
      </span>
      <button onClick={(e) => { e.stopPropagation(); backToList(); }} title="Back to the list of projects" style={{ ...BTN_SECONDARY, padding: '1px 8px' }}>
        ☰ All projects
      </button>
    </span>
  );

  return (
    <HierarchyWorkspace
      rightBottom={<AssistantSection />}
      treeNodes={project?.functions || []}
      selectedNodeId={selectedFuncId}
      onSelectNode={id => { setSelectedFuncId(id); setEditingFuncRowId(null); }}
      treeHeader={<ViewSwitcher view={view} setView={setView} onSelectRoot={() => { setSelectedFuncId(null); setEditingFuncRowId(null); }} />}
      // project === null means the fetch has not returned; an empty functions array is a
      // real answer. Without this the tree says "Loading…" forever on a new project.
      treeLoading={project === null}
      treeEmptyMessage="No functions yet — add one in the grid."
      filterPlaceholder="Filter functions…"
      storageKey="projects"
      navWidthStorageKey="inoviu:projectsNavWidth"
      defaultNavWidth={240}
      // Pinned header (2026-08-23, Alex: "vertical scrolling going to all but the
      // first header card") — Catalog/Products/Onshape already opt into
      // HierarchyWorkspace's pinned-mainHeader mode via this exact overflow:'hidden'
      // signal; Projects was the one screen that never did, so its FUNCTION row
      // scrolled away with everything else instead of staying put above it.
      mainStyle={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: `${PANE_GAP}px ${PANE_GAP}px 0` }}
      mainHeader={
        <div style={{ flexShrink: 0 }}>
          {/* Phase 5a — a FUNCTION's Onshape assembly. A function is a GROUPING
              NODE, not an instance: it has no product_id and no catalog ref, so
              structurally it is the same thing as a category, and it gets the
              same shared panel with no fork. Always applicable — a project
              function groups products, and per Alex any product may gain a model
              eventually, so there is nothing to rule out in advance.
              NOTE: the INSTANCE relation ("the configuration will have to be
              fixed per project") lives one level down on the PLACEMENT, which
              already has actual_values + frozen_snapshot. Not built here — that
              is phase 5b, and it is a different shape. */}
          {/* UX-12 / UX-18 — the function's OWN row, above its Onshape block and
              its children, exactly where Catalog and Products put theirs.
              DS-5 (2026-08-21): the page-header Breadcrumb that used to sit
              above this row is gone — every segment duplicated a control
              already on screen (the banner's Project picker, the active
              Internal tab's root-select, the tree itself), and Catalog/
              Products never had one. This is now the literal first thing in
              the panel, same as Catalog's Category row and Products' Product
              row — "Project total" moved into its own title row via `right`
              rather than vanishing. */}
          {funcHeaderRow ? (
            <HeaderRow
              title="Function"
              row={funcHeaderRow}
              columns={funcHeaderCols}
              onSave={handleSaveFuncRow}
              onEditRow={setEditingFuncHeaderRowId}
              editRowId={editingFuncHeaderRowId}
              storageKey="project-function-header"
              card
              right={projectTotalNode}
            />
          ) : (
            // No function selected — same "select…" hint Catalog/Products show at
            // their own root, so mainHeader is never blank. "Project total" is a
            // whole-PROJECT figure, not a per-function one, so it belongs here too
            // (Catalog/Products have no equivalent aggregate at their root).
            <Card storageKey="projects:section:no-selection" title="Function" right={projectTotalNode}>
              <span style={{ color: 'var(--th-text-hint)', fontSize: 'var(--fs-body)' }}>Select a function in the navigation tree.</span>
            </Card>
          )}
          {selectedFunc && (
            <CategoryOnshapePanel
              categoryId={selectedFunc._funcId}
              onshapeAssemblyRef={selectedFunc.onshape_ref || ''}
              patchUrl={`/api/functions/${selectedFunc._funcId}`}
              storageKey="project-function"
              onSaved={refreshProject}
            />
          )}
        </div>
      }
      rightPanel={
        /* UX-17.8 — configuring moved into the expanded row, so this panel is now the
           cost breakdown (top) plus the reserved AI-chat slot (bottom). Always mounted
           so the reservation is visible even with nothing selected. */
        <PlacementSidePanel
          placement={selectedPlacement}
          money={money}
          onNavigateToProduct={onNavigateToProduct}
          onNavigateToPlacement={onNavigateToPlacement}
        />
      }
      // DS-6 (2026-08-21): no longer gated on selectedPlacement — Catalog/Products/
      // Onshape all leave this at HierarchyWorkspace's default `true`, and the
      // "nothing meaningful to resize against" reasoning this used to carry (Alex,
      // 2026-08-20) doesn't hold on THIS screen specifically: rightBottom (the
      // assistant chat) is always mounted here, so the column is never actually
      // empty the way a fresh Catalog/Products load briefly is.
    >
        {/* ── Function grid (mirrors ProductHierarchyGrid in ProductsView) ── */}
        <Card storageKey="projects:section:functions" title="Functions" count={funcGridRows.length}>
          <DataGrid
            key={selectedFuncId || 'root'}
            rows={funcGridRows}
            columns={funcCols}
            onRowClick={row => {
              // §1: single-click = select (highlight); track via selectedRowId.
              // P5: also reveals the row's placements (selectedFuncId). Toggling the
              // highlight OFF does NOT clear selectedFuncId — keeps the placements
              // section from jumping/closing on a second click of the same row.
              setSelectedFuncRowId(prev => prev === row.id ? null : row.id);
              setEditingFuncRowId(null);
              setSelectedFuncId(row._funcNodeId);
            }}
            onRowDoubleClick={row => {
              // §1: double-click = drill into children
              setSelectedFuncId(row._funcNodeId);
              setSelectedFuncRowId(null);
              setEditingFuncRowId(null);
            }}
            selectedRowId={selectedFuncRowId}
            onSave={handleSaveFuncRow}
            onAdd={handleAddFunc}
            onCopy={handleCopyFunc}
            onDelete={handleDeleteFunc}
            onDeleteCheck={handleFuncDeleteCheck}
            onReorder={handleReorderFunc}
            onEditRow={id => setEditingFuncRowId(id)}
            editRowId={editingFuncRowId}
            // Auto-edit (2026-08-23, Alex: "one click to edit everywhere and remove the
            // icon") — safe here because double-click ALREADY does the exact same
            // setSelectedFuncId(row._funcNodeId) single-click used to (see above); single
            // click only loses the redundant "select without opening edit" shortcut.
            editOnRowClick
            emptyMessage={
              selectedFuncId
                ? 'No child functions. Add one below.'
                : 'No functions yet — add one below.'
            }
            storageKey="project-funcs"
          />
          {/* Parent hint for the inline add row (preserves the "parent: X" cue). */}
          <Text kind="note" style={{ display: 'block', marginTop: 3 }}>
            {selectedFuncId
              ? <>New functions are added under parent: <strong>{selectedFunc?.label}</strong></>
              : 'New functions are added at root level'}
          </Text>
        </Card>

        {/* ── Placements section (only when a function is selected) ── */}
        {selectedFuncId && (
          <Card
            storageKey="projects:section:placements"
            title="Placed products"
            count={placementRows.length}
            right={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--th-status-synced)' }}>
                  {money(selectedFunc?.total)}
                </span>
                <UnchargedTag hours={selectedFunc?.uncharged_hours} />
                {/* UX-16 — open every row's configuration at once. Free: the parameters
                    already arrived with the placements list, so this fetches nothing. */}
                {expandablePlacementIds.length > 0 && (
                  <button
                    onClick={() => setExpandedPlacementIds(
                      allPlacementsExpanded ? new Set() : new Set(expandablePlacementIds),
                    )}
                    style={BTN_SECONDARY}
                  >
                    <Chevron open={allPlacementsExpanded} />{allPlacementsExpanded ? 'Collapse all' : 'Expand all'}
                  </button>
                )}
              </div>
            }
          >
            {unchargedPlacements.count > 0 && (
              <Notice kind="warning">
                {formatQty(unchargedPlacements.hours)} hr of install hours are not charged in {unchargedPlacements.count} placed product{unchargedPlacements.count === 1 ? '' : 's'}: their product has no Labour line. Open the product and pick one in its Labour column.
              </Notice>
            )}
            <DataGrid
              rows={placementRows}
              columns={placementCols}
              expandedRowIds={expandedPlacementIds}
              renderExpandedRow={(row, { offsetOf }) => (
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  {/* THE MODEL, in the space UX-17.1 deliberately left empty ("leaving the
                      left of the row free for a fuller description or a picture"). Same
                      OnshapeSection every other surface uses — a flag for the narrow
                      variant, not a copy (UI_VISION §4). A placement has no ref of its
                      own by design: it inherits its product's and pins a configuration,
                      so this is the product's geometry.
                      Only rendered when there IS a ref — 19 of 173 products have one, and
                      an empty frame on the other 154 would be noise. When absent the
                      config keeps its original indent, so nothing moves for them. */}
                  {row.onshape_ref && (
                    <div style={{ width: offsetOf('config'), flexShrink: 0, paddingRight: 8 }}>
                      <OnshapeSection
                        onshapeRef={row.onshape_ref}
                        title="3D Model"
                        density="shrink"
                        frame={false}
                        storageKey="placement-config"
                        linkTarget="onshape"
                      >
                        {/* Honesty about what is on screen: the parameters to the right
                            are NOT reflected in this model yet. Configured exports cost a
                            fresh Onshape translation each, so they need their own gated
                            button — until then, saying so beats letting the picture imply
                            something it cannot deliver. */}
                        <Text kind="note" as="div">
                          Default configuration — not yet updated for the settings shown here.
                        </Text>
                      </OnshapeSection>
                    </div>
                  )}
                <PlacementConfigSubRow
                  // UX-17.1 (Alex: "I asked the config to be in line with the config
                  // button") — start the block under the Configuration column, leaving
                  // the left of the row free for a fuller description or a picture.
                  // With the model occupying exactly that gutter, indent 0 puts the block
                  // in the identical place — same alignment, arrived at differently.
                  indent={row.onshape_ref ? 0 : offsetOf('config')}
                  params={configurableOf(row.resolved)}
                  orphanParams={row.resolved?.orphan_params}
                  values={placementEdits[row.id] || EMPTY_EDITS}
                  onChange={(name, val) => handleRowParamChange(row.id, name, val)}
                  onSave={() => handleRowSave(row)}
                  onRevert={() => handleRowRevert(row.id)}
                  dirty={!!placementEdits[row.id]}
                  saving={savingPlacementId === row.id}
                  readOnly={!!row.frozen}
                  sizeListOptionsByUnit={sizeListOptionsByUnit}
                />
                </div>
              )}
              onRowClick={handleSelectPlacement}
              // Auto-edit (2026-08-23, Alex: "one click to edit everywhere and remove
              // the icon"). handleSelectPlacement only ever toggled a plain highlight
              // (selectedPlacementId) — the actual detail preview (renderExpandedRow,
              // below) is its own "›Configure" button with stopPropagation, independent
              // of row click, so it was never at risk. Kept handleSelectPlacement
              // reachable via double-click anyway (harmless, and WU3's external-deep-
              // link effect still calls it directly regardless of either click path).
              onRowDoubleClick={handleSelectPlacement}
              selectedRowId={selectedPlacementId}
              onSave={handleSavePlacementRow}
              onEditRow={setEditingPlacementRowId}
              editRowId={editingPlacementRowId}
              editOnRowClick
              // UX-17.3 — the Product column's editRender needs the in-cell edit path
              // (editRender only fires for inlineEditRowId), driven by the SAME row the
              // ✎ buffer uses so there is one edit mode, not two.
              inlineEditRowId={editingPlacementRowId}
              onCopy={handleCopyPlacement}
              onDelete={removePlacement}
              onReorder={(rowId, targetIndex) => moveRow(rowId, targetIndex)}
              emptyMessage='No products placed yet — use "+ Add product" below.'
              storageKey="project-placements"
              addFormLabel="Add product"
              onAdd={async (fields) => {
                const label = String(fields.label || '').trim();
                if (!addProductId) throw new Error('Pick a product first — the Product column has the picker.');
                if (!label) throw new Error('Description is required.');
                const res = await fetch('/api/placements', {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ product_id: Number(addProductId), parent_ref: selectedFuncId, label }),
                });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Could not place this product.'); }
                setAddProductId(''); setPlaceFilters({ l1: '', l2: '', l3: '' });
                refreshProject();
              }}
            />
          </Card>
        )}
    </HierarchyWorkspace>
  );
}
